/**
 * DrivingTeamsSection — the simplified ranked list of CMD teams.
 *
 * This replaces the bucket-grid `PatchTeamsRoster` per the Feb 2026
 * "simplify driving teams" feedback. Three locked changes:
 *
 *   1. ONE unified ranked list (sorted by Pulse impact descending).
 *      Color of the impact bar/value tells the bucket story — no
 *      sub-grid headers, no card sprawl.
 *   2. Vocabulary purged of "patch" — UI says **Team N** everywhere.
 *      Backend API field `patches: [...]` stays as a stable contract,
 *      but is never visible to a brand rep.
 *   3. Steady teams collapse into a single inline row ("5 steady teams
 *      firing within band · +12 impact · ▶ Expand"). Calm by default.
 *
 * Per-row content (left → right)
 * ──────────────────────────────
 *   [icon] · Team N (Role) · n creators        [impact bar] · ±N · [▲ Composite +X · ▲ Other +Y] · [Lever Button]
 *
 * Click any row → expands an inline live performance panel for that
 * team (per-creator engagement, status, platform), with a footer
 * micro-CTA to fire the lever at only the LAGGING creators in the
 * panel ("Fire Reply Storm at just the 4 lagging creators").
 *
 * Impact-on-Pulse math
 * ─────────────────────
 *   The backend `cmdData.patches[].pulse_impact` is the canonical
 *   number when present. When missing, we fallback to summing
 *   `composite_insights[X].patch_contributions[teamId].delta` across
 *   the 4 dynamic composites (diversity excluded — it doesn't move).
 *
 * Roles & lever mapping
 * ─────────────────────
 *   early_peak  → Boost Budget (Spawn Burst)
 *   bridge      → Boost Budget (Spawn Burst) OR Reply Storm if lagging
 *   long_tail   → Hero Mirror / Reply Storm
 *   general     → Tighten Sync / Reply Storm
 *   steady      → Maintain (no action button)
 */
import React, { useState, useMemo } from 'react';
import {
  TrendingUp, TrendingDown, ChevronRight, Zap, Flame, AlertTriangle,
  Activity, Clock3, Users,
} from 'lucide-react';

// ─── Theme tokens ───────────────────────────────────────────────────────
const COLOR = {
  hot:     '#4ade80',
  steady:  '#00D4FF',
  lagging: '#FF6B6B',
  purple:  '#A78BFA',
  muted:   '#6B7280',
};

const ROLE_META = {
  early_peak: { label: 'Early Peak', glyph: '↑' },
  bridge:     { label: 'Bridge',     glyph: '◊' },
  long_tail:  { label: 'Long-Tail',  glyph: '↓' },
  general:    { label: 'General',    glyph: '○' },
  steady:     { label: 'Steady',     glyph: '○' },
};

// Lever recommendation per (role, status) combo. The "label" is what
// the rep sees on the button; the "path" is the backend lever endpoint.
const LEVER_RECS = {
  early_peak_hot:  { label: 'Boost Budget', path: 'spawn-burst',   icon: '💰' },
  early_peak_lag:  { label: 'Reply Storm',  path: 'reply-storm',   icon: '⚡' },
  bridge_hot:      { label: 'Boost Budget', path: 'spawn-burst',   icon: '💰' },
  bridge_lag:      { label: 'Spawn Burst',  path: 'spawn-burst',   icon: '⚡' },
  long_tail_hot:   { label: 'Hero Mirror',  path: 'hero-mirror',   icon: '💰' },
  long_tail_lag:   { label: 'Reply Storm',  path: 'reply-storm',   icon: '⚡' },
  general_hot:     { label: 'Boost Budget', path: 'spawn-burst',   icon: '💰' },
  general_lag:     { label: 'Tighten Sync', path: 'tighten-sync',  icon: '⚡' },
};

// ─── Helpers ────────────────────────────────────────────────────────────
function compactN(n) {
  if (n == null) return '—';
  if (Math.abs(n) >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (Math.abs(n) >= 1_000)     return `${(n / 1_000).toFixed(1)}K`;
  return `${Math.round(n)}`;
}

function findLiveAssignment(handle, liveAssignments) {
  if (!handle || !liveAssignments?.length) return null;
  const lower = (handle || '').toLowerCase().replace(/^@+/, '');
  return liveAssignments.find(la => {
    const h = (la.creator_handle || '').toLowerCase().replace(/^@+/, '');
    return h === lower;
  }) || null;
}

function resolveCreator(creator_id, creators = []) {
  if (!creator_id) return null;
  return creators.find(c =>
    c.id === creator_id ||
    c.creator_id === creator_id ||
    c.email === creator_id ||
    c.handle === creator_id ||
    c.creator_handle === creator_id
  ) || null;
}

/** Classify a creator's live engagement vs cohort mean.
 *  Returns { key, label, color, Icon }. */
function classifyCreator(assignment, cohortMean) {
  if (!assignment) return { key: 'no_data',  label: 'Not assigned', color: COLOR.muted, Icon: Activity };
  if (assignment.status !== 'live') return { key: 'pending', label: 'Pending', color: COLOR.muted, Icon: Clock3 };
  const e = assignment.engagement || {};
  const total = (e.likes || 0) + (e.comments || 0) + (e.shares || 0);
  if (cohortMean > 0 && total >= 1.5 * cohortMean) return { key: 'crushing', label: 'Crushing', color: COLOR.hot, Icon: Flame };
  if (cohortMean > 0 && total <= 0.5 * cohortMean) return { key: 'lagging',  label: 'Lagging',  color: COLOR.lagging, Icon: AlertTriangle };
  return { key: 'on_pace', label: 'On pace', color: COLOR.steady, Icon: Activity };
}

/** Compute each team's Pulse impact + composite contributions.
 *  Returns rows sorted descending by impact, plus aggregate totals.
 *
 *  The backend's `composite_insights[X]` envelope ships:
 *    { leading_patch, lagging_patch, recommended_lever_id, ... }
 *    + (optional) patch_contributions: {patch_id: {delta, reason}}
 *
 *  We prefer the structured `patch_contributions` map when present,
 *  falling back to the `leading_patch` / `lagging_patch` IDs and
 *  computing the delta deterministically from the composite score.
 *  Score 75 → leader earns +7 / lagger earns -3 ; score 30 → leader
 *  earns +3 / lagger earns -6. Diversity is excluded (it doesn't move
 *  during a wave). */
function buildTeamRows(patches, compositeInsights, creators, liveAssignments) {
  const composites = ['engagement', 'reach', 'velocity', 'approval'];

  // Pre-compute per-composite leader/lagger deltas so we only touch
  // each composite's insight envelope once per render.
  const compositeMovements = {};
  for (const cmp of composites) {
    const env = compositeInsights?.[cmp];
    if (!env) continue;
    const score = typeof env.score === 'number' ? env.score : 50;
    const leaderDelta = Math.max(3, Math.round(score / 10));
    const lagDelta    = -Math.max(2, Math.round((100 - score) / 12));
    compositeMovements[cmp] = {
      leading_patch: env.leading_patch,
      lagging_patch: env.lagging_patch,
      leaderDelta,
      lagDelta,
      // Allow backend-supplied per-patch deltas to override the heuristic
      patch_contributions: env.patch_contributions || {},
    };
  }

  const rows = patches.map(patch => {
    const movements = [];
    let impactSum = 0;
    for (const cmp of composites) {
      const m = compositeMovements[cmp];
      if (!m) continue;
      const override = m.patch_contributions?.[patch.patch_id];
      let delta = 0;
      if (override && typeof override.delta === 'number') {
        delta = Math.round(override.delta);
      } else if (m.leading_patch === patch.patch_id) {
        delta = m.leaderDelta;
      } else if (m.lagging_patch === patch.patch_id) {
        delta = m.lagDelta;
      }
      if (delta === 0) continue;
      movements.push({ composite: cmp, delta });
      impactSum += delta;
    }

    // Backend `patch.pulse_impact` takes priority if shipped.
    const impact = (typeof patch.pulse_impact === 'number')
      ? Math.round(patch.pulse_impact)
      : impactSum;

    const status = impact >= 3 ? 'hot'
                 : impact <= -2 ? 'lagging'
                 : 'steady';

    const members = (patch.creator_ids || []).map(cid => {
      const c = resolveCreator(cid, creators);
      const handle = c?.handle || c?.creator_handle || cid;
      const live = findLiveAssignment(handle, liveAssignments);
      return {
        id:     cid,
        handle: (handle || '').replace(/^@+/, ''),
        avatar: c?.creator_avatar || c?.avatar_url || null,
        full:   c,
        live,
      };
    }).filter(m => !!m.handle);

    return {
      patchId:   patch.patch_id,
      role:      patch.role || 'general',
      memberCount: members.length,
      members,
      impact,
      movements,
      status,
    };
  })
  .filter(r => r.memberCount > 0)
  .sort((a, b) => b.impact - a.impact);

  const hotImpact     = rows.filter(r => r.status === 'hot').reduce((s, r) => s + r.impact, 0);
  const laggingImpact = rows.filter(r => r.status === 'lagging').reduce((s, r) => s + r.impact, 0);
  const steadyImpact  = rows.filter(r => r.status === 'steady').reduce((s, r) => s + r.impact, 0);

  const totalPositive = rows.filter(r => r.impact > 0).reduce((s, r) => s + r.impact, 0);
  const top2 = rows.filter(r => r.impact > 0).slice(0, 2).reduce((s, r) => s + r.impact, 0);
  const top2Pct = totalPositive > 0 ? Math.round((top2 / totalPositive) * 100) : 0;

  return { rows, hotImpact, laggingImpact, steadyImpact, top2Pct };
}

// ─── Composite movement chips (inline string) ───────────────────────────
function MovementString({ movements, status }) {
  if (!movements.length) {
    return <span className="text-gray-500">firing within band</span>;
  }
  const color = status === 'hot' ? COLOR.hot : status === 'lagging' ? COLOR.lagging : COLOR.steady;
  return (
    <span className="text-[11.5px] text-gray-300">
      {movements.slice(0, 3).map((m, i) => (
        <span key={m.composite}>
          {i > 0 && <span className="text-gray-600 mx-1">·</span>}
          <span className="font-black" style={{ color }}>
            {m.delta > 0 ? '▲' : '▼'} {m.composite.charAt(0).toUpperCase() + m.composite.slice(1)} {m.delta > 0 ? '+' : ''}{m.delta}
          </span>
        </span>
      ))}
    </span>
  );
}

// ─── One team row ───────────────────────────────────────────────────────
function TeamRow({ row, expanded, onToggle, onLeverFire }) {
  const role = ROLE_META[row.role] || ROLE_META.general;
  const baseColor = row.status === 'hot' ? COLOR.hot
                  : row.status === 'lagging' ? COLOR.lagging
                  : COLOR.steady;

  // Lever recommendation — only for hot or lagging rows
  const leverKey = row.status === 'hot'
    ? `${row.role}_hot`
    : row.status === 'lagging' ? `${row.role}_lag`
    : null;
  const lever = leverKey ? (LEVER_RECS[leverKey] || LEVER_RECS.general_hot) : null;

  const impactPct = Math.min(95, Math.max(8, Math.abs(row.impact) * 4));
  const isNegative = row.impact < 0;

  return (
    <div
      data-testid={`team-row-${row.patchId}`}
      data-team-status={row.status}
      className="group"
    >
      <button
        type="button"
        onClick={onToggle}
        data-testid={`team-row-${row.patchId}-toggle`}
        aria-expanded={expanded}
        className={`w-full grid items-center gap-3 text-left rounded-[10px] transition-colors ${expanded ? '' : 'hover:bg-[#1a1f2b]'}`}
        style={{
          gridTemplateColumns: '30px minmax(140px, 1.4fr) minmax(180px, 1.4fr) 64px minmax(220px, 2fr) 170px',
          background: expanded ? '#1a1f2b' : '#181A20',
          border: '1px solid #2E3240',
          borderLeft: `3px solid ${baseColor}`,
          padding: '11px 14px',
          borderBottomLeftRadius:  expanded ? 0 : 10,
          borderBottomRightRadius: expanded ? 0 : 10,
        }}
      >
        {/* badge */}
        <span
          className="w-7 h-7 rounded-lg flex items-center justify-center text-[13px] font-black flex-shrink-0"
          style={{
            background: `${baseColor}1A`,
            color: baseColor,
            border: `1px solid ${baseColor}40`,
          }}
        >
          {role.glyph}
        </span>

        {/* identity */}
        <div className="min-w-0">
          <p className="text-[13px] font-black text-white leading-tight">
            Team {row.patchId}
            <span className="text-[9.5px] text-gray-500 font-bold ml-1.5 uppercase tracking-[0.12em]">
              · {role.label}
            </span>
          </p>
          <p className="text-[10.5px] text-gray-500 leading-tight mt-1">
            {row.memberCount} creators
          </p>
        </div>

        {/* impact bar */}
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-[9px] text-gray-500 font-black uppercase tracking-[0.12em] flex-shrink-0 w-9">
            Pulse
          </span>
          <div
            className="flex-1 h-[5px] rounded-full overflow-hidden"
            style={{ background: '#0F0F0F', border: '1px solid #1a1f2b', display: 'flex', justifyContent: isNegative ? 'flex-end' : 'flex-start' }}
          >
            <div
              className="h-full rounded-full"
              style={{ width: `${impactPct}%`, background: baseColor }}
            />
          </div>
        </div>

        {/* impact value */}
        <span
          className="text-[18px] font-black tabular-nums tracking-[-0.02em] text-right"
          style={{ color: baseColor }}
          data-testid={`team-row-${row.patchId}-impact`}
        >
          {row.impact > 0 ? '+' : ''}{row.impact}
        </span>

        {/* composite movements */}
        <div className="min-w-0 truncate">
          <MovementString movements={row.movements} status={row.status} />
        </div>

        {/* action */}
        <div className="flex items-center justify-end gap-2">
          {lever ? (
            <span
              onClick={(ev) => { ev.stopPropagation(); onLeverFire(lever.path, row); }}
              data-testid={`team-row-${row.patchId}-lever`}
              role="button"
              tabIndex={0}
              onKeyDown={(ev) => { if (ev.key === 'Enter' || ev.key === ' ') { ev.stopPropagation(); onLeverFire(lever.path, row); } }}
              className="text-[11px] font-black px-2.5 py-1.5 rounded-md uppercase tracking-[0.06em] cursor-pointer transition-transform hover:scale-[1.02]"
              style={{
                background: row.status === 'hot' ? `${baseColor}14` : 'rgba(167,139,250,0.10)',
                color:      row.status === 'hot' ? baseColor : COLOR.purple,
                border:     row.status === 'hot' ? `1px solid ${baseColor}55` : '1px solid rgba(167,139,250,0.50)',
              }}
            >
              {lever.icon} {lever.label}
            </span>
          ) : (
            <span className="text-[10.5px] text-gray-500 font-black uppercase tracking-[0.08em]">
              — Maintain
            </span>
          )}
          <ChevronRight
            className={`h-3.5 w-3.5 text-gray-500 transition-transform ${expanded ? 'rotate-90 text-white' : ''}`}
          />
        </div>
      </button>

      {/* Drilldown panel */}
      {expanded && (
        <TeamDrilldown row={row} onLeverFire={onLeverFire} lever={lever} baseColor={baseColor} />
      )}
    </div>
  );
}

// ─── Inline live performance drilldown ──────────────────────────────────
function TeamDrilldown({ row, onLeverFire, lever, baseColor }) {
  // Cohort mean from this team's live engagements (used to classify rows).
  const cohortMean = useMemo(() => {
    const totals = row.members
      .map(m => m.live?.engagement)
      .filter(Boolean)
      .map(e => (e.likes || 0) + (e.comments || 0) + (e.shares || 0));
    if (!totals.length) return 0;
    return totals.reduce((a, b) => a + b, 0) / totals.length;
  }, [row.members]);

  const visibleRows = row.members.slice(0, 8);
  const overflow    = Math.max(0, row.members.length - visibleRows.length);
  const classifications = row.members.map(m => classifyCreator(m.live, cohortMean));
  const laggingCount    = classifications.filter(c => c.key === 'lagging').length;
  const liveCount       = row.members.filter(m => m.live?.status === 'live').length;
  const pendingCount    = row.members.length - liveCount;

  return (
    <div
      data-testid={`team-drilldown-${row.patchId}`}
      className="rounded-b-[10px] overflow-hidden"
      style={{
        background: '#0F1115',
        border:     '1px solid #2E3240',
        borderTop:  'none',
        borderLeft: `3px solid ${baseColor}`,
      }}
    >
      <div
        className="px-4 py-2.5 flex items-center justify-between"
        style={{ background: `${baseColor}0d`, borderBottom: `1px solid ${baseColor}22` }}
      >
        <span className="text-[10.5px] uppercase tracking-[0.18em] font-black text-gray-300">
          Live performance · Team {row.patchId}
        </span>
        <span className="text-[10.5px] tabular-nums text-gray-400">
          <span style={{ color: baseColor }}>{liveCount}</span> firing
          {pendingCount > 0 && <> · <span className="text-gray-500">{pendingCount}</span> pending</>}
          {laggingCount > 0 && <> · <span style={{ color: COLOR.lagging }}>{laggingCount}</span> lagging</>}
        </span>
      </div>

      <ul className="divide-y divide-[#1a1d24]">
        {visibleRows.map((m, idx) => {
          const status = classifications[idx];
          const e = m.live?.engagement || {};
          const engTotal = (e.likes || 0) + (e.comments || 0) + (e.shares || 0);
          const platform = m.live?.platform || '—';
          const StatusIcon = status.Icon;
          return (
            <li
              key={m.handle}
              data-testid={`team-drilldown-${row.patchId}-row-${m.handle}`}
              className="px-4 py-2 flex items-center gap-3"
            >
              <div
                className="w-6 h-6 rounded-full overflow-hidden flex-shrink-0 flex items-center justify-center text-[9px] font-black text-white"
                style={{ background: '#2E3240' }}
              >
                {m.avatar
                  ? <img src={m.avatar} alt={m.handle} className="w-full h-full object-cover" />
                  : m.handle.charAt(0).toUpperCase()}
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-[11.5px] font-bold text-white truncate">@{m.handle}</p>
                <p className="text-[9.5px] text-gray-500 uppercase tracking-[0.08em] truncate">
                  {platform} · {(m.live?.role || row.role).replace('_', ' ')}
                </p>
              </div>
              <div className="hidden sm:flex flex-col items-end tabular-nums flex-shrink-0">
                <span className="text-[12px] font-black text-white">{compactN(engTotal)}</span>
                <span className="text-[9.5px] text-gray-500">{compactN(e.views || 0)} views</span>
              </div>
              <span
                className="text-[9px] font-black px-2 py-0.5 rounded uppercase tracking-[0.08em] inline-flex items-center gap-1 flex-shrink-0"
                style={{
                  background: `${status.color}14`,
                  color:      status.color,
                  border:     `1px solid ${status.color}40`,
                }}
              >
                <StatusIcon className="h-2.5 w-2.5" />
                {status.label}
              </span>
            </li>
          );
        })}
        {overflow > 0 && (
          <li className="px-4 py-2 text-[10.5px] text-gray-500 text-center">
            + {overflow} more creator{overflow === 1 ? '' : 's'}
          </li>
        )}
      </ul>

      {/* Micro-CTA: fire the lever at just the lagging creators */}
      {laggingCount > 0 && lever && (
        <button
          type="button"
          onClick={() => onLeverFire(lever.path, row, { onlyLagging: true })}
          data-testid={`team-drilldown-${row.patchId}-target-lagging`}
          className="w-full px-4 py-2.5 text-[11px] font-black uppercase tracking-[0.06em] transition-colors hover:brightness-110 text-center"
          style={{
            background: 'rgba(167,139,250,0.08)',
            color: COLOR.purple,
            borderTop: '1px solid rgba(167,139,250,0.20)',
          }}
        >
          {lever.icon} Fire {lever.label} at just the {laggingCount} lagging creator{laggingCount === 1 ? '' : 's'}
        </button>
      )}
    </div>
  );
}

// ─── Top summary band ───────────────────────────────────────────────────
function SummaryBand({ rows, hotImpact, laggingImpact, top2Pct }) {
  const hotN = rows.filter(r => r.status === 'hot').length;
  const lagN = rows.filter(r => r.status === 'lagging').length;
  return (
    <div
      data-testid="driving-teams-summary"
      className="flex flex-wrap items-center gap-3 px-4 py-3 rounded-xl mb-4"
      style={{
        background: 'linear-gradient(135deg, rgba(74,222,128,0.08), rgba(0,212,255,0.04))',
        border: '1px solid rgba(74,222,128,0.20)',
      }}
    >
      <span className="text-[13px]" style={{ color: '#e2e8f0' }}>
        <b style={{ color: COLOR.hot }}>{hotN} hot team{hotN === 1 ? '' : 's'}</b>
        {' '}powering most of your Pulse
        {lagN > 0 && (
          <> · <b style={{ color: COLOR.lagging }}>{lagN} lagging</b> need intervention</>
        )}
        {top2Pct > 0 && (
          <> · top 2 driving <b className="text-white">{top2Pct}%</b> of momentum</>
        )}
      </span>
      <div className="flex items-center gap-2 ml-auto">
        <span
          className="text-[10.5px] font-black px-2.5 py-0.5 rounded-full"
          style={{ background: '#0F0F0F', border: '1px solid #2E3240', color: '#cbd5e1' }}
        >
          <span style={{ color: COLOR.hot }}>+{hotImpact}</span> hot impact
        </span>
        {laggingImpact < 0 && (
          <span
            className="text-[10.5px] font-black px-2.5 py-0.5 rounded-full"
            style={{ background: '#0F0F0F', border: '1px solid #2E3240', color: '#cbd5e1' }}
          >
            <span style={{ color: COLOR.lagging }}>{laggingImpact}</span> lagging drag
          </span>
        )}
      </div>
    </div>
  );
}

// ─── Steady-collapse toggle ─────────────────────────────────────────────
function SteadyToggle({ steadyCount, steadyImpact, expanded, onToggle }) {
  if (steadyCount === 0) return null;
  return (
    <button
      type="button"
      onClick={onToggle}
      data-testid="driving-teams-steady-toggle"
      className="w-full flex items-center justify-between px-3.5 py-2.5 rounded-[10px] text-[11.5px] hover:bg-[#1a1f2b]"
      style={{
        background: 'rgba(0,0,0,0.25)',
        border: '1px dashed #2E3240',
        color: '#cbd5e1',
      }}
    >
      <span>
        <span className="text-gray-500">○</span>
        {' '}
        <b className="text-white">{steadyCount} steady team{steadyCount === 1 ? '' : 's'}</b>
        {' '}firing within band · contributing
        {' '}<b className="text-white">{steadyImpact >= 0 ? '+' : ''}{steadyImpact} impact</b>
      </span>
      <span className="text-[10.5px] font-black uppercase tracking-[0.06em]" style={{ color: COLOR.purple }}>
        {expanded ? '▾ Collapse' : '▶ Expand'}
      </span>
    </button>
  );
}

// ─── Main export ────────────────────────────────────────────────────────
export default function DrivingTeamsSection({
  patches = [],
  creators = [],
  compositeInsights = {},
  liveAssignments = [],
  onLeverFire = () => {},
}) {
  const [expandedRowId, setExpandedRowId] = useState(null);
  const [steadyExpanded, setSteadyExpanded] = useState(false);

  const summary = useMemo(
    () => buildTeamRows(patches, compositeInsights, creators, liveAssignments),
    [patches, compositeInsights, creators, liveAssignments]
  );

  if (!summary.rows.length) {
    return (
      <p className="text-[12px] text-gray-500" data-testid="driving-teams-empty">
        Teams will appear once enough creators have fired posts to form behavioural patterns.
      </p>
    );
  }

  const visibleRows = summary.rows.filter(r => steadyExpanded || r.status !== 'steady');
  const steadyCount = summary.rows.filter(r => r.status === 'steady').length;
  const steadyInsertIndex = (() => {
    // Insert the steady-toggle right between hot and lagging rows.
    const lastHotIdx = [...visibleRows].reverse().findIndex(r => r.status === 'hot');
    const lastHot = lastHotIdx === -1 ? -1 : visibleRows.length - 1 - lastHotIdx;
    return lastHot + 1;
  })();

  const togglePatch = (patchId) =>
    setExpandedRowId(curr => (curr === patchId ? null : patchId));

  return (
    <div data-testid="driving-teams-section" className="flex flex-col gap-1.5">
      <SummaryBand
        rows={summary.rows}
        hotImpact={summary.hotImpact}
        laggingImpact={summary.laggingImpact}
        top2Pct={summary.top2Pct}
      />

      {visibleRows.map((row, i) => (
        <React.Fragment key={row.patchId}>
          {/* Steady toggle slots in between hot and lagging sections */}
          {i === steadyInsertIndex && steadyCount > 0 && (
            <div className="my-1">
              <SteadyToggle
                steadyCount={steadyCount}
                steadyImpact={summary.steadyImpact}
                expanded={steadyExpanded}
                onToggle={() => setSteadyExpanded(v => !v)}
              />
            </div>
          )}
          <TeamRow
            row={row}
            expanded={expandedRowId === row.patchId}
            onToggle={() => togglePatch(row.patchId)}
            onLeverFire={onLeverFire}
          />
        </React.Fragment>
      ))}

      {/* If all rows are hot/lagging (no steady), still render the toggle
          at the end if there are any steady teams hiding. */}
      {steadyCount > 0 && steadyInsertIndex >= visibleRows.length && (
        <div className="mt-1">
          <SteadyToggle
            steadyCount={steadyCount}
            steadyImpact={summary.steadyImpact}
            expanded={steadyExpanded}
            onToggle={() => setSteadyExpanded(v => !v)}
          />
        </div>
      )}
    </div>
  );
}
